import { createTheme, alpha } from '@mui/material/styles';

/*
 * GoTruTax app theme: the MUI mirror of src/styles/app/tokens.css (Hallmark Furrow at app scope,
 * accent = brand blue #2563EB). This object is the declared theme block for MUI: its hex values are
 * the sRGB conversions of the oklch tokens, because MUI's colour helpers cannot parse oklch.
 */
export const tokens = {
    paper: '#f5f1ea', // --color-paper
    paper2: '#fffdfa', // --color-paper-2
    paper3: '#e9e4d9', // --color-paper-3
    sheet: '#fefdfa', // --color-sheet
    ink: '#14110f', // --color-ink
    ink2: '#2c2825', // --color-ink-2
    muted: '#58514c', // --color-muted
    rule: '#dbd7ce', // --color-rule
    grid: '#e2ddd4', // --color-grid
    accent: '#2563eb', // --color-accent (brand blue)
    accentDeep: '#134ac5', // --color-accent-deep
    accentSoft: '#dce8ff', // --color-accent-soft
    accentLight: '#6f9cf5',
    accentInk: '#fffdfa', // --color-accent-ink
    green: '#246e3a', // --color-green
    greenSoft: '#d3edd7', // --color-green-soft
    warn: '#be7100', // --color-warn
    warnSoft: '#fce4c4', // --color-warn-soft
    error: '#be2323', // --color-error
    errorSoft: '#ffdeda', // --color-error-soft
    fontDisplay: '"Red Hat Display", "Red Hat Text", ui-sans-serif, system-ui, sans-serif',
    fontBody: '"Red Hat Text", ui-sans-serif, system-ui, sans-serif',
    fontMono: '"Red Hat Mono", ui-monospace, monospace',
    radiusCard: 10,
    radiusControl: 6,
    durMicro: '120ms',
    durShort: '220ms',
    easeOut: 'cubic-bezier(0.16, 1, 0.3, 1)',
};

const t = tokens;
const ring = `0 0 0 2px ${t.paper2}, 0 0 0 4px ${t.accent}`;
const quietShadow = `0 1px 2px ${alpha(t.ink, 0.06)}`;

// App type scale at a 1.2 ratio from a 16px body (Hallmark app screens)
const display = { fontFamily: t.fontDisplay, fontWeight: 600, letterSpacing: '-0.02em', color: t.ink };

export const theme = createTheme({
    palette: {
        mode: 'light',
        primary: { main: t.accent, light: t.accentLight, dark: t.accentDeep, contrastText: t.accentInk },
        secondary: { main: t.ink2, light: t.muted, dark: t.ink, contrastText: t.paper2 },
        success: { main: t.green, light: t.greenSoft, dark: '#1a5229', contrastText: t.paper2 },
        warning: { main: t.warn, light: t.warnSoft, dark: '#8f5400', contrastText: t.paper2 },
        error: { main: t.error, light: t.errorSoft, dark: '#8f1a1a', contrastText: t.paper2 },
        info: { main: t.accent, light: t.accentSoft, dark: t.accentDeep, contrastText: t.accentInk },
        background: { default: t.paper, paper: t.paper2 },
        text: { primary: t.ink, secondary: t.muted, disabled: alpha(t.muted, 0.6) },
        divider: t.rule,
        grey: { 50: '#faf8f4', 100: '#f2eee7', 200: t.paper3, 300: t.rule, 400: '#b9b3a9', 500: '#8f887f', 600: '#6f6861', 700: t.muted, 800: t.ink2, 900: t.ink },
        action: { hover: alpha(t.ink, 0.04), selected: alpha(t.accent, 0.08), focus: alpha(t.accent, 0.12) },
    },
    shape: { borderRadius: t.radiusControl },
    typography: {
        fontFamily: t.fontBody,
        h1: { ...display, fontSize: '2.074rem', lineHeight: 1.1 },
        h2: { ...display, fontSize: '1.728rem', lineHeight: 1.15 },
        h3: { ...display, fontSize: '1.44rem', lineHeight: 1.2 },
        h4: { ...display, fontSize: '1.728rem', lineHeight: 1.15 },
        h5: { ...display, fontSize: '1.2rem', lineHeight: 1.25, letterSpacing: '-0.01em' },
        h6: { ...display, fontSize: '1.0625rem', lineHeight: 1.3, letterSpacing: '-0.005em' },
        subtitle1: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.45 },
        subtitle2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.45 },
        body1: { fontSize: '1rem', lineHeight: 1.55 },
        body2: { fontSize: '0.875rem', lineHeight: 1.55 },
        caption: { fontSize: '0.8125rem', lineHeight: 1.45, letterSpacing: 0 },
        overline: { fontSize: '0.8125rem', lineHeight: 1.45, letterSpacing: 0, textTransform: 'none', fontWeight: 500 },
        button: { textTransform: 'none', fontWeight: 500, letterSpacing: 0 },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: { backgroundColor: t.paper, color: t.ink, fontFamily: t.fontBody },
                'code, kbd, pre, samp': { fontFamily: t.fontMono },
            },
        },
        MuiButtonBase: { defaultProps: { disableRipple: true } },
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                root: {
                    borderRadius: t.radiusControl,
                    minHeight: 36,
                    padding: '6px 16px',
                    fontWeight: 500,
                    transition: `background-color ${t.durMicro} ${t.easeOut}, color ${t.durMicro} ${t.easeOut}, border-color ${t.durMicro} ${t.easeOut}, box-shadow ${t.durMicro} ${t.easeOut}`,
                    '&.Mui-focusVisible': { boxShadow: ring },
                    '&.Mui-disabled': { opacity: 0.55, cursor: 'not-allowed', pointerEvents: 'auto' },
                },
                sizeSmall: { minHeight: 32, padding: '4px 12px', fontSize: '0.875rem' },
                sizeLarge: { minHeight: 44, padding: '8px 22px', fontSize: '1rem' },
                contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
                containedPrimary: { '&:hover': { backgroundColor: t.accentDeep } },
                outlined: { borderColor: alpha(t.ink, 0.22), color: t.ink, backgroundColor: t.paper2, '&:hover': { borderColor: t.ink2, backgroundColor: t.paper2 } },
                outlinedPrimary: { borderColor: alpha(t.accent, 0.5), color: t.accentDeep, '&:hover': { borderColor: t.accent, backgroundColor: alpha(t.accent, 0.04) } },
                text: { '&:hover': { backgroundColor: alpha(t.ink, 0.04) } },
                textPrimary: { color: t.accentDeep },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: t.radiusControl,
                    transition: `background-color ${t.durMicro} ${t.easeOut}`,
                    '&.Mui-focusVisible': { boxShadow: ring },
                },
            },
        },
        MuiPaper: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
                root: { backgroundImage: 'none' },
                outlined: { borderColor: t.rule },
                elevation1: { boxShadow: quietShadow, border: `1px solid ${t.rule}` },
                elevation2: { boxShadow: quietShadow, border: `1px solid ${t.rule}` },
                elevation3: { boxShadow: quietShadow, border: `1px solid ${t.rule}` },
                elevation0: { border: `1px solid ${t.rule}` },
            },
        },
        MuiCard: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
                root: { borderRadius: t.radiusCard, border: `1px solid ${t.rule}`, boxShadow: 'none', backgroundColor: t.paper2 },
            },
        },
        MuiCardContent: { styleOverrides: { root: { padding: 24, '&:last-child': { paddingBottom: 24 } } } },
        MuiAppBar: {
            defaultProps: { elevation: 0 },
            styleOverrides: { root: { boxShadow: 'none', backgroundColor: t.paper2, color: t.ink, borderBottom: `1px solid ${t.rule}` } },
        },
        MuiDialog: { styleOverrides: { paper: { borderRadius: 14, border: 'none', boxShadow: `0 24px 48px -20px ${alpha(t.ink, 0.4)}` } } },
        MuiDialogTitle: { styleOverrides: { root: { fontFamily: t.fontDisplay, fontWeight: 600, fontSize: '1.2rem', letterSpacing: '-0.01em' } } },
        MuiMenu: { styleOverrides: { paper: { border: `1px solid ${t.rule}`, boxShadow: `0 12px 28px -14px ${alpha(t.ink, 0.35)}` } } },
        MuiPopover: { styleOverrides: { paper: { border: `1px solid ${t.rule}` } } },
        MuiMenuItem: { styleOverrides: { root: { fontSize: '0.9375rem', minHeight: 40, '&.Mui-focusVisible': { backgroundColor: alpha(t.accent, 0.08) } } } },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: t.paper2,
                    borderRadius: t.radiusControl,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(t.ink, 0.22) },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: t.ink2 },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: t.accent, borderWidth: 1 },
                    '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(t.accent, 0.18)}` },
                },
            },
        },
        MuiInputLabel: { styleOverrides: { root: { color: t.muted } } },
        MuiChip: {
            styleOverrides: {
                root: { borderRadius: t.radiusControl, fontWeight: 500, letterSpacing: 0 },
                label: { textTransform: 'none' },
                outlined: { borderColor: t.rule, backgroundColor: t.paper2 },
                colorSuccess: { backgroundColor: t.greenSoft, color: '#1a5229' },
                colorWarning: { backgroundColor: t.warnSoft, color: '#7a4700' },
                colorError: { backgroundColor: t.errorSoft, color: '#8f1a1a' },
                colorInfo: { backgroundColor: t.accentSoft, color: t.accentDeep },
                colorPrimary: { backgroundColor: t.accentSoft, color: t.accentDeep },
                outlinedPrimary: { backgroundColor: t.paper2, color: t.accentDeep, borderColor: alpha(t.accent, 0.4) },
            },
        },
        MuiTableHead: { styleOverrides: { root: { '& .MuiTableCell-root': { backgroundColor: 'transparent' } } } },
        MuiTableCell: {
            styleOverrides: {
                root: { borderBottom: `1px solid ${t.rule}`, fontVariantNumeric: 'tabular-nums' },
                head: { fontWeight: 500, fontSize: '0.8125rem', color: t.muted, textTransform: 'none', letterSpacing: 0, whiteSpace: 'nowrap', borderBottom: `1px solid ${t.ink}` },
            },
        },
        MuiTableRow: { styleOverrides: { root: { '&.MuiTableRow-hover:hover': { backgroundColor: alpha(t.ink, 0.025) } } } },
        MuiTabs: { styleOverrides: { root: { minHeight: 44, borderBottom: `1px solid ${t.rule}` }, indicator: { height: 2, backgroundColor: t.accent } } },
        MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 500, minHeight: 44, color: t.muted, '&.Mui-selected': { color: t.ink }, '&.Mui-focusVisible': { boxShadow: `inset ${ring}` } } } },
        MuiAlert: {
            styleOverrides: {
                root: { borderRadius: t.radiusCard, alignItems: 'flex-start' },
                standardInfo: { backgroundColor: t.accentSoft, color: t.ink },
                standardSuccess: { backgroundColor: t.greenSoft, color: t.ink },
                standardWarning: { backgroundColor: t.warnSoft, color: t.ink },
                standardError: { backgroundColor: t.errorSoft, color: t.ink },
            },
        },
        MuiLinearProgress: { styleOverrides: { root: { borderRadius: 999, backgroundColor: t.paper3, height: 6 }, bar: { borderRadius: 999 } } },
        MuiTooltip: { defaultProps: { enterDelay: 800, enterNextDelay: 400 }, styleOverrides: { tooltip: { backgroundColor: t.ink, fontSize: '0.8125rem', fontWeight: 400 } } },
        MuiDivider: { styleOverrides: { root: { borderColor: t.rule } } },
        MuiSwitch: { styleOverrides: { switchBase: { '&.Mui-focusVisible + .MuiSwitch-track': { boxShadow: ring } } } },
        MuiLink: { styleOverrides: { root: { color: t.accentDeep, textUnderlineOffset: '0.16em', '&.Mui-focusVisible': { outline: `2px solid ${t.accent}`, outlineOffset: 2 } } } },
        MuiAvatar: { styleOverrides: { root: { fontFamily: t.fontDisplay, fontWeight: 600 } } },
        MuiDrawer: { styleOverrides: { paper: { backgroundColor: t.paper2, color: t.ink, borderRight: `1px solid ${t.rule}` } } },
        MuiListItem: {
            styleOverrides: {
                root: {
                    '&.Mui-selected': { backgroundColor: alpha(t.accent, 0.08) },
                    '&.Mui-selected:hover': { backgroundColor: alpha(t.accent, 0.12) },
                },
            },
        },
    },
});

export default theme;
